import { useEffect, useRef, useCallback } from 'react';
import { postIntegrityEvents } from '../services/api';

/**
 * useIntegritySignals — captures proof-of-human "integrity" signals during a
 * timed work sample and flushes them to the hash-chained, append-only audit log.
 *
 * Design rules (from the verified-talent research):
 *  - LOG-ONLY. These signals never block the candidate; they inform recruiter
 *    review. Failing one is "needs review", never an auto-reject.
 *  - NO biometrics here (camera/keystroke-timing come later, behind consent).
 *  - Typed-vs-pasted is tracked via `beforeinput`, EXCLUDING IME composition so
 *    dictation / IME / assistive-tech users are not penalized.
 *
 * Events are buffered and flushed in order (batches keep the server-side hash
 * chain race-free). Returns `finalize(text)` to call at submit time.
 */
export function useIntegritySignals(submissionId, { answerRef } = {}) {
  const bufRef = useRef([]);
  const typedRef = useRef(0);   // chars inserted by genuine typing
  const pastedRef = useRef(0);  // chars inserted by paste
  const flushing = useRef(false);

  const push = useCallback((type, category, meta = {}) => {
    bufRef.current.push({ type, category, meta, client_ts: new Date().toISOString() });
  }, []);

  const flush = useCallback(async () => {
    if (!submissionId || flushing.current || bufRef.current.length === 0) return;
    flushing.current = true;
    const batch = bufRef.current.splice(0, bufRef.current.length);
    try {
      await postIntegrityEvents(submissionId, batch);
    } catch (e) {
      bufRef.current.unshift(...batch); // re-buffer; best-effort, never blocks
    } finally {
      flushing.current = false;
    }
  }, [submissionId]);

  // proctoring + automation signals
  useEffect(() => {
    if (!submissionId) return;
    push('session_start', 'provenance', { ua: navigator.userAgent });

    const onVis = () => push(document.hidden ? 'tab_hidden' : 'tab_visible', 'proctoring', {});
    const onBlur = () => push('window_blur', 'proctoring', {});
    const onFocus = () => push('window_focus', 'proctoring', {});
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);

    // BotD — automation/agent detection (MIT). Lazy, best-effort, corroborating only.
    let cancelled = false;
    (async () => {
      try {
        const mod = await import('@fingerprintjs/botd');
        const botd = await mod.load();
        const r = await botd.detect();
        if (!cancelled) push('bot_verdict', 'automation', { bot: !!r.bot, ...(r.bot && r.botKind ? { kind: r.botKind } : {}) });
      } catch (e) { /* package missing or blocked — skip silently */ }
    })();

    const timer = setInterval(flush, 5000);
    const onHide = () => flush();
    window.addEventListener('pagehide', onHide);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pagehide', onHide);
      clearInterval(timer);
      flush();
    };
  }, [submissionId, push, flush]);

  // typed-vs-pasted provenance on the answer field
  useEffect(() => {
    const el = answerRef && answerRef.current;
    if (!el) return;
    const onPaste = (e) => {
      const txt = (e.clipboardData || window.clipboardData)?.getData('text') || '';
      pastedRef.current += txt.length;
      push('paste', 'provenance', { chars: txt.length });
    };
    const onCopy = () => push('copy', 'provenance', {});
    const onCut = () => push('cut', 'provenance', {});
    const onBeforeInput = (e) => {
      const t = e.inputType || '';
      if (t === 'insertText' && e.data) {
        typedRef.current += e.data.length;
      } else if (t.startsWith('insertFrom') && t !== 'insertFromComposition' && t !== 'insertFromPaste') {
        push('bulk_insert', 'provenance', { inputType: t }); // drop, autofill, etc.
      }
    };
    el.addEventListener('paste', onPaste);
    el.addEventListener('copy', onCopy);
    el.addEventListener('cut', onCut);
    el.addEventListener('beforeinput', onBeforeInput);
    return () => {
      el.removeEventListener('paste', onPaste);
      el.removeEventListener('copy', onCopy);
      el.removeEventListener('cut', onCut);
      el.removeEventListener('beforeinput', onBeforeInput);
    };
  }, [answerRef, push]);

  const finalize = useCallback(async (finalText) => {
    push('session_submit', 'provenance', {
      typed_chars: typedRef.current,
      paste_chars: pastedRef.current,
      final_chars: (finalText || '').length,
    });
    await flush();
  }, [push, flush]);

  return { finalize, flushNow: flush };
}

export default useIntegritySignals;
